<template>
  <v-container fluid class="order-create-page pa-0">
    <v-alert
      v-if="selectedCustomer && selectedCustomer.alert"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-3"
      v-model="selectedCustomer.alert"
      color="red"
    >
      <strong>{{ selectedCustomer.note }}</strong>
    </v-alert>

    <div class="floating-order-bar" :class="{ show: showFloatingBar }">
      <div>
        <strong>Create Order</strong>
        <span v-if="selectedCustomer && selectedCustomer.name">
          · {{ selectedCustomer.name }}
        </span>
      </div>

      <div class="floating-actions">
        <v-btn x-small rounded outlined @click.stop.prevent="cancel">
          Cancel
        </v-btn>

        <v-btn
          x-small
          rounded
          color="primary"
          dark
          :disabled="Order.OrderDetails.length === 0"
          @click="saveOrder"
        >
          {{ invoiceTotal | currency }} Save
        </v-btn>
      </div>
    </div>

    <v-card outlined class="order-create-card">
      <div class="order-create-header">
        <div>
          <div class="order-create-title">
            Create In-House Order

            <v-chip
              v-if="
                selectedCustomer &&
                selectedCustomer.PriceTier &&
                currentBusiness &&
                currentBusiness.metadata &&
                !currentBusiness.metadata.isSingleCustomerPricing
              "
              x-small
              class="ml-2 font-weight-bold"
              color="red"
              text-color="white"
            >
              {{ selectedCustomer.PriceTier.name }}
            </v-chip>
          </div>

          <div class="order-create-subtitle">
            Phone order or staff-created order. Products are not removed until invoice is created.
          </div>
        </div>

        <div class="order-create-actions">
          <v-btn rounded outlined :small="$vuetify.breakpoint.smAndDown" @click.stop.prevent="cancel">
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            rounded
            dark
            :small="$vuetify.breakpoint.smAndDown"
            :disabled="Order.OrderDetails.length === 0"
            @click="saveOrder"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ invoiceTotal | currency }} Save
          </v-btn>
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <div class="field-label">Customer</div>
            <AutoComplete
              :initValue="initCustomer"
              v-model="selectedCustomer"
              :errorMessages="selectCustomerError"
            />
          </v-col>

          <v-col cols="12" md="3">
            <div class="field-label">Sales Rep</div>
            <SalesMan v-model="selectedUser" :initValue="initUser" />
          </v-col>

          <v-col cols="12" md="2">
            <div class="field-label">Date</div>
            <DateSelector label="Date" v-model="Order.date" />
          </v-col>

          <v-col
            cols="12"
            md="3"
            v-if="
              !(
                currentBusiness &&
                currentBusiness.metadata &&
                currentBusiness.metadata.isSingleCustomerPricing
              )
            "
          >
            <div class="field-label">Price Tier</div>
            <v-select
              :items="tiers"
              item-text="name"
              item-value="id"
              v-model="Order.PriceTierId"
              label="Select Price Tier"
              outlined
              dense
              hide-details
              clearable
              @change="onPriceTierChange"
            />
          </v-col>

          <v-col cols="12">
            <div class="note-row">
              <v-dialog v-model="orderNote" width="500">
                <template v-slot:activator="{ on }">
                  <v-btn small rounded outlined v-on="on">
                    <v-icon left small>mdi-note-text-outline</v-icon>
                    Order Note
                  </v-btn>
                </template>

                <v-card class="note-dialog-card">
                  <div class="dialog-header">
                    <div>
                      <div class="dialog-title">Order Note</div>
                      <div class="dialog-subtitle">Add internal note for this order.</div>
                    </div>

                    <v-btn icon small @click="orderNote = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>

                  <v-card-text class="pa-4">
                    <v-textarea
                      outlined
                      dense
                      hide-details
                      auto-grow
                      rows="4"
                      v-model="Order.note"
                      label="Order Note"
                    />
                  </v-card-text>

                  <v-divider />

                  <v-card-actions class="dialog-actions">
                    <v-spacer />
                    <v-btn color="primary" rounded dark @click="orderNote = false">
                      Done
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <div v-if="Order.note" class="note-preview">
                {{ Order.note }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-if="selectedCustomer && selectedCustomer.id" class="mt-3">
      <ProductSearchBar
        :price-tier-id="Order.PriceTierId"
        :selectedCustomer="selectedCustomer"
        @submit="addRow"
      />
    </div>

    <v-card v-else outlined class="start-card mt-3">
      <v-card-text class="text-center pa-6">
        <v-icon size="44" color="primary">mdi-account-search-outline</v-icon>
        <div class="start-title">Select customer to start order</div>
        <div class="start-subtitle">After selecting customer, product search and cart will appear.</div>
      </v-card-text>
    </v-card>

    <div v-if="selectedCustomer && selectedCustomer.id" class="mt-3">
      <v-card outlined class="order-cart-card">
        <div class="cart-header">
          <div>
            <div class="cart-title">Order Cart</div>
            <div class="cart-subtitle">
              {{ Order.OrderDetails.length }} row{{ Order.OrderDetails.length === 1 ? "" : "s" }} · {{ totalQty }} item{{ totalQty === 1 ? "" : "s" }}
            </div>
          </div>

          <div class="cart-total">
            {{ invoiceTotal | currency }}
          </div>
        </div>

        <v-card-text class="pa-4">
          <v-data-table
            :mobile-breakpoint="0"
            :headers="headers"
            hide-default-footer
            :items-per-page="1000"
            no-data-text="No item on this order yet"
            :items="Order.OrderDetails"
            class="order-cart-table"
          >
            <template v-slot:item="{ item, index }">
              <tr>
                <td class="image-cell">
                  <v-avatar tile v-if="item.imageUrl" size="46">
                    <v-img contain :src="item.imageUrl" alt="Product image" />
                  </v-avatar>
                </td>

                <td>
                  <div class="cart-name-row">
                    <v-btn icon small @click="deleteItem(index)">
                      <v-icon color="red">mdi-delete-outline</v-icon>
                    </v-btn>

                    <v-text-field
                      v-model="item.name"
                      outlined
                      hide-details
                      :disabled="lockProductName"
                      @focus="$event.target.select()"
                      dense
                    />
                  </div>
                </td>

                <td
                  v-if="
                    currentBusiness &&
                    currentBusiness.metadata &&
                    currentBusiness.metadata.boxCountOnInventory
                  "
                  class="text-center"
                >
                  {{ item.unitCountInBox || "--" }}
                </td>

                <td class="text-center qty-cell">
                  <v-text-field
                    v-model="item.qty"
                    :disabled="item.imeiNumber ? true : false"
                    outlined
                    @blur="checkPrice(item)"
                    class="centered-input"
                    hide-details
                    dense
                    @focus="$event.target.select()"
                  />
                </td>

                <td class="text-right price-cell">
                  <v-text-field
                    v-model="item.price"
                    outlined
                    class="right-input"
                    hide-details
                    @blur="checkPrice(item)"
                    @focus="$event.target.select()"
                    dense
                  />
                </td>

                <td class="text-right font-weight-bold">
                  {{ item.total | currency }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <div class="mobile-save-bar">
      <div>
        <div class="mobile-total-label">Order Total</div>
        <div class="mobile-total-value">{{ invoiceTotal | currency }}</div>
      </div>

      <v-btn
        color="primary"
        rounded
        dark
        :disabled="Order.OrderDetails.length === 0"
        @click="saveOrder"
      >
        Save
      </v-btn>
    </div>
  </v-container>
</template>

<script>
const { validationMixin } = require("vuelidate");
const { required, minValue } = require("vuelidate/lib/validators");

import DateSelector from "@/components/DateSelector";
import AutoComplete from "@/modules/Customer/components/AutoComplete";
import SalesMan from "@/modules/User/components/AutoComplete.vue";
import ProductSearchBar from "@/modules/Order/components/InHouse/ProductSearchBar.vue";
import priceTierService from "@/modules/Product/PriceTier/service.js";
import orderService from "./service";
import { mapGetters } from "vuex";

export default {
  name: "create-order",

  components: {
    DateSelector,
    AutoComplete,
    SalesMan,
    ProductSearchBar,
  },

  mixins: [validationMixin],

  data() {
    return {
      draftKey: "runningInHouseOrder",
      initCustomer: null,
      initUser: null,
      selectedUser: null,
      selectedCustomer: null,
      orderNote: false,
      loading: false,
      showFloatingBar: false,
      Order: {
        date: this.$moment().toISOString(),
        note: null,
        CustomerId: null,
        OrderDetails: [],
        PriceTierId: null,
      },
      tiers: [],
      headers: [],
    };
  },

  validations: {
    Order: {
      CustomerId: {
        required,
        minValue: minValue(1),
      },
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),

    lockProductName() {
      if (!this.currentBusiness.metadata.hasOwnProperty("lockProductName")) {
        return true;
      }

      return !!this.currentBusiness.metadata.lockProductName;
    },

    totalQty() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.qty || 0);
      }, 0);
    },

    selectCustomerError() {
      const errors = [];

      if (!this.$v.Order.CustomerId.$dirty) return errors;

      if (!this.$v.Order.CustomerId.required || !this.$v.Order.CustomerId.minValue) {
        errors.push("Please select customer");
      }

      return errors;
    },

    invoiceTotal() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },
  },

  watch: {
    Order: {
      handler() {
        this.saveDraft();
      },
      deep: true,
    },

    selectedUser() {
      this.saveDraft();
    },

    selectedCustomer(val) {
      if (val && val.id) {
        this.Order.CustomerId = val.id;

        if (val.PriceTierId) {
          this.Order.PriceTierId = val.PriceTierId;
          this.onPriceTierChange();
        }

        this.initUser = val.SalesRep || this.currentUser;
        this.selectedUser = this.initUser;
      } else {
        this.Order.CustomerId = null;
        this.initUser = this.currentUser;
      }

      this.saveDraft();
    },
  },

  async created() {
    this.restoreDraft();

    if (this.$route.query && this.$route.query.CustomerId) {
      this.initCustomer = this.$route.query.CustomerId;
    }

    await this.getTiers();
    this.loadHeaders();
  },

  mounted() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  methods: {
    handleScroll() {
      this.showFloatingBar = window.scrollY > 220;
    },

    saveDraft() {
      localStorage.setItem(
        this.draftKey,
        JSON.stringify({
          Order: this.Order,
          selectedCustomer: this.selectedCustomer,
          selectedUser: this.selectedUser,
        })
      );
    },

    restoreDraft() {
      const draft = localStorage.getItem(this.draftKey);

      if (!draft) return;

      try {
        const parsed = JSON.parse(draft);

        if (parsed.Order) {
          this.Order = parsed.Order;
          this.Order.date = this.$moment().toISOString();
        }

        if (parsed.Order && parsed.Order.CustomerId) {
          this.initCustomer = parsed.Order.CustomerId;
        }

        if (parsed.selectedCustomer) {
          this.selectedCustomer = parsed.selectedCustomer;
        }

        if (parsed.selectedUser) {
          this.selectedUser = parsed.selectedUser;
          this.initUser = parsed.selectedUser;
        }
      } catch (e) {
        console.warn("Failed to parse in-house order draft", e);
      }
    },

    loadHeaders() {
      this.headers = [
        {
          text: "Image",
          align: "left",
          width: "60",
          value: "image",
          sortable: false,
        },
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: false,
        },
      ];

      if (this.currentBusiness && this.currentBusiness.metadata && this.currentBusiness.metadata.boxCountOnInventory) {
        this.headers.push({
          text: "Unit Count",
          align: "center",
          value: "unitCountInBox",
          sortable: false,
        });
      }

      this.headers.push(
        {
          text: "Qty",
          align: "center",
          value: "qty",
          width: "120",
          sortable: false,
        },
        {
          text: "Price",
          align: "right",
          value: "price",
          width: "130",
          sortable: false,
        },
        {
          text: "Total",
          align: "right",
          value: "total",
          width: "130",
          sortable: false,
        }
      );
    },

    checkPrice(item) {
      const qty = Number(item.qty || 0);
      const price = Number(item.price || 0);
      const unitCount = Number(item.unitCountInBox || 0);

      if (unitCount > 0) {
        item.total = Number((qty * unitCount * price).toFixed(2));
      } else {
        item.total = Number((qty * price).toFixed(2));
      }
    },

    async getTierById(id) {
      try {
        if (!id) return null;
        const response = await priceTierService.getById(id);
        return response.data;
      } catch (error) {
        console.error("Error fetching tier by ID:", error);
        return null;
      }
    },

    calculateTierPrice(originalPrice, selectedTier, productId) {
      if (!selectedTier) return Number(originalPrice || 0);

      let newPrice = Number(originalPrice || 0);

      if (selectedTier.discountType === "percentage") {
        const rate = Number(selectedTier.rate || 0);
        newPrice = newPrice - newPrice * (rate / 100);
      }

      if (selectedTier.discountType === "fixed") {
        const productTier = Array.isArray(selectedTier.ProductPrices)
          ? selectedTier.ProductPrices.find((p) => Number(p.ProductId) === Number(productId))
          : null;

        if (productTier) {
          newPrice = Number(productTier.price || 0);
        }
      }

      return Number(newPrice.toFixed(2));
    },

    async onPriceTierChange() {
      const selectedTier = await this.getTierById(this.Order.PriceTierId);

      this.Order.OrderDetails.forEach((detail) => {
        const originalPrice = detail.Product ? detail.Product.price : detail.price;

        detail.price = this.calculateTierPrice(
          originalPrice,
          selectedTier,
          detail.ProductId
        );

        this.checkPrice(detail);
      });

      this.$forceUpdate();
    },

    async getTiers() {
      try {
        const response = await priceTierService.getAll();
        this.tiers = response.data || [];
      } catch (e) {
        console.error("Failed to load tiers", e);
      }
    },

    cancel() {
      if (this.Order.OrderDetails.length > 0) {
        this.$swal({
          title: "Are you sure?",
          text: "This will cancel the current order and all items will be lost.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "No, keep editing",
          confirmButtonText: "Yes, cancel it",
        }).then((result) => {
          if (result.value) {
            localStorage.removeItem(this.draftKey);
            this.$router.go(-1);
          }
        });

        return;
      }

      localStorage.removeItem(this.draftKey);
      this.$router.go(-1);
    },

    saveOrder() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (this.Order.OrderDetails.length === 0) {
        return;
      }

      if (this.selectedUser && this.selectedUser.id) {
        this.Order.SalesRepUserId = this.selectedUser.id;
      }

      this.loading = true;

      return orderService
        .create(this.Order)
        .then(() => {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "success",
              title: "Order was created!",
            });

          localStorage.removeItem(this.draftKey);
          this.$router.go(-1);
        })
        .catch((err) => {
          const message =
            err && err.data && err.data.message
              ? err.data.message
              : "Unable to create order";

          this.$swal("Create Order", message, "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteItem(index) {
      this.$swal({
        title: "Are you sure?",
        text: "Remove this item from order?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value) {
          this.Order.OrderDetails.splice(index, 1);
        }
      });
    },

    addRow(val) {
      if (!val) return;

      this.Order.OrderDetails.push({
        ProductId: val.id,
        Product: val,
        name: val.Bar.name,
        price: val.Bar.price,
        total: val.Bar.price * val.Bar.qty,
        unitCountInBox: val.unitCountInBox,
        boxQty: val.Bar.boxQty,
        qty: val.Bar.qty,
        imageUrl: val.imageUrl,
      });
    },
  },
};
</script>

<style scoped>
.order-create-page {
  background: #f8fafc;
  min-height: 100%;
  padding-bottom: 84px !important;
}

.order-create-card,
.order-cart-card,
.start-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.order-create-header,
.cart-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.order-create-title,
.cart-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.order-create-subtitle,
.cart-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.order-create-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.note-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.note-preview {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 8px 12px;
  font-size: 13px;
  color: #475569;
  max-width: 600px;
}

.cart-total {
  font-size: 24px;
  font-weight: 900;
  color: #0f172a;
}

.order-cart-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.image-cell {
  width: 60px;
}

.cart-name-row {
  display: grid;
  grid-template-columns: 40px minmax(260px, 1fr);
  gap: 8px;
  align-items: center;
}

.qty-cell {
  width: 120px;
}

.price-cell {
  width: 130px;
}

.start-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 10px;
}

.start-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

.floating-order-bar {
  position: sticky;
  top: 64px;
  z-index: 10;
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  margin-bottom: 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.floating-order-bar.show {
  display: flex;
}

.floating-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.note-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
}

.dialog-actions {
  padding: 12px 16px;
}

.mobile-save-bar {
  display: none;
}

.centered-input input {
  text-align: center;
}

.right-input input {
  text-align: right;
}

@media (max-width: 960px) {
  .cart-name-row {
    grid-template-columns: 40px minmax(180px, 1fr);
  }
}

@media (max-width: 600px) {
  .order-create-page {
    padding: 10px !important;
    padding-bottom: 84px !important;
  }

  .order-create-header,
  .cart-header {
    padding: 14px;
  }

  .order-create-title,
  .cart-title {
    font-size: 19px;
  }

  .order-create-actions {
    display: none;
  }

  .floating-order-bar {
    display: none !important;
  }

  .cart-total {
    font-size: 20px;
  }

  .mobile-save-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 12;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
  }

  .mobile-total-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
  }

  .mobile-total-value {
    font-size: 18px;
    font-weight: 900;
    color: #0f172a;
  }
}
</style>